import { useEffect, useState } from 'react';
import { loadRuntimeConfig } from './lib/config';
import { createCheckoutClient } from './lib/sdk';
import { applyTheme } from './theme/applyTheme';
import { AppContext, type AppContextValue } from './lib/AppContext';
import { RouterProvider, useRouter } from './router';
import { ErrorBanner, Spinner } from './components/ui';
import { Header } from './components/Header';
import { EventListPage } from './pages/EventListPage';
import { EventPage } from './pages/EventPage';
import { CheckoutPage } from './pages/CheckoutPage';
import { ConfirmationPage } from './pages/ConfirmationPage';

export function App() {
  const [state, setState] = useState<
    | { status: 'loading' }
    | { status: 'error'; message: string }
    | { status: 'ready'; value: AppContextValue }
  >({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;
    loadRuntimeConfig()
      .then((config) => {
        if (cancelled) return;
        applyTheme(config.theme);
        const client = createCheckoutClient(config);
        setState({ status: 'ready', value: { config, client } });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setState({
          status: 'error',
          message: err instanceof Error ? err.message : 'Could not load checkout configuration.',
        });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (state.status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner label="Loading checkout…" />
      </div>
    );
  }

  if (state.status === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="max-w-md w-full">
          <ErrorBanner message={state.message} />
        </div>
      </div>
    );
  }

  return (
    <AppContext.Provider value={state.value}>
      <RouterProvider>
        <Shell />
      </RouterProvider>
    </AppContext.Provider>
  );
}

function Shell() {
  const { route } = useRouter();
  return (
    <div className="min-h-screen">
      <Header />
      <main id="main-content" className="mx-auto max-w-3xl px-4 py-8 sm:px-6">
        {route.name === 'events' && <EventListPage />}
        {route.name === 'event' && <EventPage eventId={route.eventId} />}
        {route.name === 'checkout' && (
          <CheckoutPage checkoutSessionId={route.checkoutSessionId} accessToken={route.accessToken} />
        )}
        {route.name === 'order' && (
          <ConfirmationPage orderId={route.orderId} accessToken={route.accessToken} />
        )}
        {route.name === 'not-found' && <p className="text-slate-600">Page not found.</p>}
      </main>
    </div>
  );
}
